<script lang="ts">
  /**
   * Toaster — renders toast notifications (app.css has .toast-enter animation).
   */
  import { toasts, removeToast, type ToastKind } from '$stores/toast'
  import { _ } from 'svelte-i18n'

  const KIND_ICON: Record<ToastKind, string> = {
    success: '✓',
    error: '✕',
    info: 'ℹ',
  }
</script>

{#if $toasts.length > 0}
  <div class="toaster" aria-live="polite">
    {#each $toasts as toast (toast.id)}
      <div class="toast toast-{toast.kind} toast-enter" role="status">
        <span class="toast-icon" aria-hidden="true">{KIND_ICON[toast.kind]}</span>
        <span class="toast-message">{toast.message}</span>
        {#if toast.action}
          <button
            class="toast-action"
            onclick={() => {
              toast.action?.run()
              removeToast(toast.id)
            }}
          >
            {toast.action.label}
          </button>
        {/if}
        <button class="toast-dismiss" aria-label={$_('common.close')} onclick={() => removeToast(toast.id)}>
          &times;
        </button>
      </div>
    {/each}
  </div>
{/if}

<style>
  .toaster {
    position: fixed;
    top: calc(var(--header-height) + var(--space-3));
    right: var(--space-4);
    z-index: 2000;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    max-width: 360px;
  }

  .toast {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
  }

  .toast-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    border-radius: 50%;
  }

  .toast-success .toast-icon {
    background: var(--color-success);
  }
  .toast-error .toast-icon {
    background: var(--color-danger);
  }
  .toast-info .toast-icon {
    background: var(--color-accent);
  }

  .toast-message {
    flex: 1;
    font-size: var(--text-sm);
    color: var(--color-text-primary);
  }

  .toast-dismiss {
    border: none;
    background: transparent;
    color: var(--color-text-tertiary);
    font-size: 16px;
    cursor: pointer;
    line-height: 1;
  }

  .toast-dismiss:hover {
    color: var(--color-text-primary);
  }

  .toast-action {
    flex-shrink: 0;
    border: 1px solid var(--color-accent);
    background: transparent;
    color: var(--color-accent);
    font-size: var(--text-sm);
    font-weight: 600;
    padding: 4px 12px;
    border-radius: var(--radius-md);
    cursor: pointer;
  }

  .toast-action:hover {
    background: var(--color-accent-light);
  }

  @media (max-width: 767px) {
    .toaster {
      top: var(--space-3);
      right: var(--space-3);
      left: var(--space-3);
      max-width: none;
    }
  }
</style>
